import { useEffect, useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { getTimeRangeValue } from '../../utils';

import { useS3Uploader } from './../useS3Uploader';
import { useTableData } from './../useTableData';
import { ConnectedUsers } from '../../pages/DoctorManagement/types';
import {
  getOrganisationStats,
  getOrganisationDashboardActivity,
  getOrganisationDashboardLineActivity,
  getOrganisationManagementList
} from '../../services';

import { getTimestamps } from '../../helper';

export interface OrganisationDashboardRow {
  sNo: number;
  organisationName: string;
  registrationNumber: string;
  no: number;
  organisationId?: string;
  verificationDate: string;
  lastUpdatedAt: string;
  phoneNumber: string;
  image?: string;
  connectedUsers: ConnectedUsers[];
}

export const useOrganisation = (
  selectedValueDoughnutChart: string,
  selectedValueVerificationTrends: string,
  rowsPerPage: number = 10,
  page: number = 0
) => {
  const {
    tableRows,
    setTableRows,
    hasMore,
    setHasMore,
    totalCount,
    setTotalCount,
    pageTimestamps,
    setPageTimestamps
  } = useTableData<OrganisationDashboardRow>();

  const timestamps = useMemo(() => {
    if (!selectedValueDoughnutChart) return { startTimestamp: '', endTimestamp: '' };
    return getTimestamps(selectedValueDoughnutChart) ?? { startTimestamp: '', endTimestamp: '' };
  }, [selectedValueDoughnutChart]);

  const { generateSignedUrl } = useS3Uploader();

  /* ---------------------------- DASHBOARD QUERIES ---------------------------- */

  const organisationDetailsQuery = useQuery({
    queryKey: ['organisationDashboardDetails'],
    queryFn: async () => {
      const details = await getOrganisationStats();
      return {
        totalOrganisationsCount: details?.totalOrganisations?.aggregate?.count || 0,
        practiseAdminCount: details?.practiseAdmin?.aggregate?.count || 0,
        subPractiseCount: details?.totalPractice?.aggregate?.count || 0
      };
    }
  });

  const organisationActivityQuery = useQuery({
    queryKey: ['organisationDashboardActivity', timestamps.startTimestamp, timestamps.endTimestamp],
    queryFn: async () => {
      const activityDetails = await getOrganisationDashboardActivity({
        startTimestamp: timestamps.startTimestamp,
        endTimestamp: timestamps.endTimestamp
      });

      const active = activityDetails?.activeOrganisations?.aggregate?.count || 0;
      const total = activityDetails?.totalorganisations?.aggregate?.count || 0;

      return {
        activeOrganisations: active,
        inActiveOrganisations: total > 0 ? total - active : 0,
        totalOrganisations: total
      };
    },
    placeholderData: keepPreviousData,
    enabled: !!timestamps.startTimestamp && !!timestamps.endTimestamp // optional: only run if timestamps are valid
  });

  const organisationLineActivityQuery = useQuery({
    queryKey: ['organisationDashboardLineActivity', selectedValueVerificationTrends],
    queryFn: async () => {
      const rangeType = getTimeRangeValue(selectedValueVerificationTrends as string);
      const lineData = await getOrganisationDashboardLineActivity({ rangeType });
      return lineData?.get_new_organisations_active_count_v1;
    },
    placeholderData: keepPreviousData,
    enabled: !!selectedValueVerificationTrends
  });

  /* ---------------------------- TABLE QUERY ---------------------------- */

  const tableQuery = useQuery({
    queryKey: ['organisationTable', page, rowsPerPage],
    queryFn: async () => {
      const prevTimestamp = page === 0 ? undefined : pageTimestamps.get(page - 1);
      const result = await getOrganisationManagementList(
        rowsPerPage,
        prevTimestamp,
        generateSignedUrl
      );

      const rows = await Promise.all(
        (result.data || []).map(async (item, index) => {
          return {
            ...item,
            sNo: page * rowsPerPage + index + 1,
            connectedUsers: item.connectedUsers.map((u) => ({
              ...u,
              image: u.image || '' // ensure it's always a string
            }))
          } as OrganisationDashboardRow; // optional type assertion
        })
      );

      return { ...result, rows };
    },
    placeholderData: keepPreviousData
  });

  useEffect(() => {
    if (!tableQuery.data) return;

    const data = tableQuery.data;
    setTableRows(data.rows);
    setHasMore(data.hasMore || false);
    setTotalCount(data.totalCount || 0);

    if (data.lastUpdatedAt) {
      setPageTimestamps((prev) => new Map(prev).set(page, data.lastUpdatedAt as string));
    }
  }, [tableQuery.data, page]);

  /* ---------------------------- DERIVED DATA ---------------------------- */

  const totalOrgCount =
    (organisationActivityQuery.data?.activeOrganisations || 0) +
    (organisationActivityQuery.data?.inActiveOrganisations || 0);

  /* ---------------------------- RETURN ---------------------------- */

  return {
    organisationDetails: organisationDetailsQuery.data,
    organisationActivity: organisationActivityQuery.data,
    organisationLineActivity: organisationLineActivityQuery.data,
    totalOrgCount,
    tableRows,
    totalCount,
    hasMore,
    tableLoading: tableQuery.isFetching,
    loading: false,
    error:
      organisationDetailsQuery.error ||
      organisationActivityQuery.error ||
      organisationLineActivityQuery.error ||
      tableQuery.error
  };
};
